import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Package } from "lucide-react";

import {
  Box,
  Paper,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Chip,
  CircularProgress,
  IconButton,
  TablePagination,
  Tooltip,
} from "@mui/material";
import DataTable from "@/components/mui/DataTable";

const handleChangePage = (event, newPageZeroBased) => {
  const newPage = newPageZeroBased + 1;
  fetchData(newPage, pagination.perPage);
};

const handleChangeRowsPerPage = (event) => {
  const newPerPage = parseInt(event.target.value, 10);
  setPagination((prev) => ({
    ...prev,
    perPage: newPerPage,
    page: 1,
  }));
  fetchData(1, newPerPage);
};

export default function Products() {
  const normalizeNumber = (value) => {
    if (typeof value === "number" && Number.isFinite(value)) {
      return value;
    }

    if (typeof value === "string") {
      const trimmedValue = value.trim();
      if (!trimmedValue) {
        return 0;
      }

      const sanitizedValue = trimmedValue.replace(/[^\d,.-]/g, "");

      if (sanitizedValue.includes(",")) {
        const normalizedBrValue = Number(
          sanitizedValue.replace(/\./g, "").replace(",", "."),
        );
        return Number.isFinite(normalizedBrValue) ? normalizedBrValue : 0;
      }

      const normalizedValue = Number(sanitizedValue);
      return Number.isFinite(normalizedValue) ? normalizedValue : 0;
    }

    const normalized = Number(value);
    return Number.isFinite(normalized) ? normalized : 0;
  };

  const formatMoneyInput = (value) =>
    new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(normalizeNumber(value));

  const parseMoneyInput = (value) => {
    const digits = String(value || "").replace(/\D/g, "");
    return digits ? Number(digits) / 100 : 0;
  };

  const formatPercentageInput = (value) =>
    `${normalizeNumber(value).toFixed(2).replace(".", ",")}%`;

  const parsePercentageInput = (value) => {
    const digits = String(value || "").replace(/\D/g, "");
    return digits ? Number(digits) / 100 : 0;
  };

  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    barcode: "",
    category_id: "",
    supplier_id: "",
    unit: "un",
    cost_price: 0,
    sale_price: 0,
    profit_percentage: 0,
    min_stock: 10,
    stock: 0,
  });
  const [maskedValues, setMaskedValues] = useState({
    cost_price: formatMoneyInput(0),
    sale_price: formatMoneyInput(0),
    profit_percentage: formatPercentageInput(0),
  });

  const [pagination, setPagination] = useState({
    page: 1,      // 1-based (Laravel)
    perPage: 15,  // default do backend
    total: 0,
    lastPage: 0,
  });

  const [filters, setFilters] = useState({
    search: "",
    sortBy: "name",
    sortDir: "asc",
  });

  const safeProducts = Array.isArray(products) ? products : [];
  const safeCategories = Array.isArray(categories) ? categories : [];
  const safeSuppliers = Array.isArray(suppliers) ? suppliers : [];

  useEffect(() => {
    fetchData(1, pagination.perPage);
  }, []);

  const columns = [
    {
      id: "name",
      label: "Nome",
      sortable: true,
      render: (product) => (
        <Typography fontWeight={600}>{product.name}</Typography>
      ),
    },
    {
      id: "barcode",
      label: "Código",
      sortable: true,
      render: (product) => product.barcode || "-",
    },
    { id: "unit", label: "Unidade", sortable: true },
    {
      id: "cost_price",
      label: "Preço Custo",
      sortable: true,
      render: (product) => formatMoney(product.cost_price),
    },
    {
      id: "sale_price",
      label: "Preço Venda",
      sortable: true,
      render: (product) => formatMoney(product.sale_price),
    },
    {
      id: "profit_percentage",
      label: "Lucro %",
      sortable: true,
      render: (product) => {
        const profit = normalizeNumber(product.profit_percentage);
        return profit ? `${profit.toFixed(1)}%` : "-";
      },
    },
    {
      id: "stock",
      label: "Estoque",
      sortable: true,
      render: (product) => {
        const stock = Number(product.stock || 0);
        const minStock = Number(product.min_stock || 0);
        const color =
          stock < minStock
            ? "error"
            : stock === minStock
              ? "warning"
              : "success";

        return <Chip label={stock} color={color} size="small" />;
      },
    },
    {
      id: "min_stock",
      label: "Est. Mín",
      sortable: true,
    },
    {
      id: "actions",
      label: "Ações",
      sortable: false,
      render: (product) => (
        <Box sx={{ display: "flex", gap: 1 }}>
          <Tooltip title="Editar produto">
            <IconButton
              size="small"
              color="primary"
              onClick={() => handleEdit(product)}
              data-testid={`edit-product-${product.id}`}
            >
              <Pencil size={14} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Remover produto">
            <IconButton
              size="small"
              color="error"
              onClick={() => handleDelete(product.id)}
              data-testid={`delete-product-${product.id}`}
            >
              <Trash2 size={14} />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  const handleSort = (columnId) => {
    // se clicar na mesma coluna, alterna asc/desc
    const isSameColumn = filters.sortBy === columnId;
    const newDir =
      isSameColumn && filters.sortDir === "asc" ? "desc" : "asc";

    const override = { sortBy: columnId, sortDir: newDir };

    setFilters((prev) => ({ ...prev, ...override }));
    fetchData(1, pagination.perPage, override);
  };

  const applySearchFilter = () => {
    fetchData(1, pagination.perPage, { search: filters.search?.trim() || "" });
  };


  const fetchData = async (page = 1, perPage = pagination.perPage, overrideFilters = {}) => {
    try {
      setLoading(true);

      const currentFilters = { ...filters, ...overrideFilters };

      const [productsRes, categoriesRes, suppliersRes] = await Promise.all([
        axios.get(`${API}/products`, {
          params: {
            page,
            per_page: perPage,
            search: currentFilters.search || undefined,
            sort_by: currentFilters.sortBy,
            sort_dir: currentFilters.sortDir,
          },
        }),
        axios.get(`${API}/categories`),
        axios.get(`${API}/suppliers`),
      ]);

      const productsData = productsRes.data;
      const normalizedCategories = Array.isArray(categoriesRes.data?.data)
        ? categoriesRes.data.data
        : Array.isArray(categoriesRes.data)
          ? categoriesRes.data
          : [];
      const normalizedSuppliers = Array.isArray(suppliersRes.data?.data)
        ? suppliersRes.data.data
        : Array.isArray(suppliersRes.data)
          ? suppliersRes.data
          : [];

      if (Array.isArray(productsData)) {
        setProducts(productsData);
        setPagination({
          page: 1,
          perPage: productsData.length,
          total: productsData.length,
          lastPage: 1,
        });
      } else {
        setProducts(Array.isArray(productsData?.data) ? productsData.data : []);
        setPagination({
          page: productsData?.current_page || 1,
          perPage: productsData?.per_page || perPage,
          total: productsData?.total || 0,
          lastPage: productsData?.last_page || 1,
        });
      }

      setCategories(normalizedCategories);
      setSuppliers(normalizedSuppliers);

      // Atualiza filtros locais quando usar override
      setFilters(currentFilters);
    } catch (error) {
      toast.error("Erro ao carregar dados");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingProduct) {
        await axios.put(`${API}/products/${editingProduct.id}`, formData);
        toast.success("Produto atualizado!");
      } else {
        await axios.post(`${API}/products`, formData);
        toast.success("Produto criado!");
      }
      setShowModal(false);
      resetForm();
      fetchData(pagination.page, pagination.perPage);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao salvar produto");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Deseja realmente excluir este produto?")) return;
    try {
      await axios.delete(`${API}/products/${id}`);
      toast.success("Produto excluído!");
      fetchData(pagination.page, pagination.perPage);
    } catch (error) {
      toast.error("Erro ao excluir produto");
    }
  };

  const handleEdit = (product) => {
    setEditingProduct(product);
    setFormData({
      name: product.name,
      barcode: product.barcode || "",
      category_id: product.category_id || "",
      supplier_id: product.supplier_id || "",
      unit: product.unit,
      cost_price: product.cost_price,
      sale_price: product.sale_price,
      profit_percentage: product.profit_percentage || 0,
      min_stock: product.min_stock,
      stock: product.stock ?? 0,
    });
    setMaskedValues({
      cost_price: formatMoneyInput(product.cost_price),
      sale_price: formatMoneyInput(product.sale_price),
      profit_percentage: formatPercentageInput(product.profit_percentage || 0),
    });
    setShowModal(true);
  };

  const resetForm = () => {
    setFormData({
      name: "",
      barcode: "",
      category_id: "",
      supplier_id: "",
      unit: "un",
      cost_price: 0,
      sale_price: 0,
      profit_percentage: 0,
      min_stock: 10,
      stock: 0,
    });
    setMaskedValues({
      cost_price: formatMoneyInput(0),
      sale_price: formatMoneyInput(0),
      profit_percentage: formatPercentageInput(0),
    });
    setEditingProduct(null);
  };

  // Cálculo bidirecional de preço/lucro
  const handleCostChange = (value) => {
    const cost = parseMoneyInput(value);
    setFormData(prev => {
      const profit = normalizeNumber(prev.profit_percentage);
      const currentSalePrice = normalizeNumber(prev.sale_price);
      const newSalePrice = cost > 0 && profit > 0 ? cost * (1 + profit / 100) : currentSalePrice;
      const normalizedSalePrice = normalizeNumber(newSalePrice.toFixed(2));

      setMaskedValues((prevMasked) => ({
        ...prevMasked,
        cost_price: formatMoneyInput(cost),
        sale_price: formatMoneyInput(normalizedSalePrice),
      }));

      return { ...prev, cost_price: cost, sale_price: normalizedSalePrice };
    });
  };

  const handleSalePriceChange = (value) => {
    const sale = parseMoneyInput(value);
    setFormData(prev => {
      const cost = normalizeNumber(prev.cost_price);
      const profit = cost > 0 ? ((sale - cost) / cost) * 100 : 0;
      const normalizedProfit = normalizeNumber(profit.toFixed(2));

      setMaskedValues((prevMasked) => ({
        ...prevMasked,
        sale_price: formatMoneyInput(sale),
        profit_percentage: formatPercentageInput(normalizedProfit),
      }));

      return { ...prev, sale_price: sale, profit_percentage: normalizedProfit };
    });
  };

  const handleProfitChange = (value) => {
    const profit = parsePercentageInput(value);
    setFormData(prev => {
      const cost = normalizeNumber(prev.cost_price);
      const currentSalePrice = normalizeNumber(prev.sale_price);
      const newSalePrice = cost > 0 ? cost * (1 + profit / 100) : currentSalePrice;
      const normalizedSalePrice = normalizeNumber(newSalePrice.toFixed(2));

      setMaskedValues((prevMasked) => ({
        ...prevMasked,
        profit_percentage: formatPercentageInput(profit),
        sale_price: formatMoneyInput(normalizedSalePrice),
      }));

      return { ...prev, profit_percentage: profit, sale_price: normalizedSalePrice };
    });
  };

  const formatMoney = (value) =>
    `R$ ${normalizeNumber(value).toFixed(2).replace(".", ",")}`;

  const handleChangePage = (event, newPageZeroBased) => {
    const newPage = newPageZeroBased + 1; // TablePagination é 0-based
    fetchData(newPage, pagination.perPage);
  };

  const handleChangeRowsPerPage = (event) => {
    const newPerPage = parseInt(event.target.value, 10);
    setPagination((prev) => ({
      ...prev,
      perPage: newPerPage,
      page: 1,
    }));
    fetchData(1, newPerPage);
  };

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: 200,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box data-testid="products-page">
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" gap={2} mb={4}>
        <Box>
          <Typography variant="h4" fontWeight="bold">
            Produtos
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Listas de produtos registrados
          </Typography>
        </Box>

        <Button
          variant="contained"
          startIcon={<Plus size={16} />}
          onClick={() => {
            resetForm();
            setShowModal(true);
          }}
        >
          Novo Produto
        </Button>
      </Box>

      <Paper className="data-table-container">
        <Box
          className="table-header"
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            borderBottom: "1px solid",
            borderColor: "divider",
            gap: 2,
            px: 3,
            py: 2,
          }}
        >
          {/* Filtros */}
          <TextField
            label="Buscar por nome ou código"
            variant="outlined"
            size="small"
            fullWidth
            value={filters.search}
            onChange={(e) =>
              setFilters((prev) => ({ ...prev, search: e.target.value }))
            }
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                applySearchFilter();
              }
            }}
          />

          <Button
            variant="outlined"
            onClick={() => fetchData(1, pagination.perPage)}
          >
            Limpar
          </Button>

          <Button
            variant="contained"
            onClick={applySearchFilter}
          >
            Filtrar
          </Button>
        </Box>

        {/* Tabela / Empty */}
        {pagination.total === 0 ? (
          <Box
            sx={{
              py: 6,
              px: 3,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              textAlign: "center",
              gap: 1.5,
            }}
          >
            <Package size={64} />
            <Typography variant="h6">Nenhum produto cadastrado</Typography>
            <Typography variant="body2">
              Comece adicionando seu primeiro produto
            </Typography>
          </Box>
        ) : (
          <>
            <DataTable
              columns={columns}
              rows={safeProducts}
              loading={loading}
              pagination={{
                page: pagination.page - 1,
                rowsPerPage: pagination.perPage,
                total: pagination.total,
                onPageChange: handleChangePage,
                onRowsPerPageChange: handleChangeRowsPerPage,
                rowsPerPageOptions: [5, 10, 15, 25, 50],
              }}
              orderBy={filters.sortBy}
              orderDirection={filters.sortDir}
              onRequestSort={handleSort}
              getRowId={(row) => row.id}
              getRowTestId={(row) => `product-row-${row.id}`}
              emptyMessage="Nenhum produto cadastrado"
            />
          </>
        )}
      </Paper>

      {/* Dialog de criação/edição */}
      <Dialog
        open={showModal}
        onClose={() => {
          setShowModal(false);
          resetForm();
        }}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>
          {editingProduct ? "Editar Produto" : "Novo Produto"}
        </DialogTitle>

        <DialogContent dividers>
          <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1 }}>
            {/* GRID DO FORMULÁRIO: 2 colunas no desktop, 1 no mobile */}
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  md: "repeat(2, minmax(0, 1fr))",
                },
                columnGap: 2,
                rowGap: 2,
              }}
            >
              {/* Linha 1: Nome / Código de Barras */}
              <TextField
                label="Nome"
                fullWidth
                required
                value={formData.name}
                onChange={(e) =>
                  setFormData({ ...formData, name: e.target.value })
                }
                data-testid="product-name-input"
              />

              <TextField
                label="Código de Barras"
                fullWidth
                value={formData.barcode}
                onChange={(e) =>
                  setFormData({ ...formData, barcode: e.target.value })
                }
                data-testid="product-barcode-input"
              />

              {/* Linha 2: Categoria / Fornecedor */}
              <FormControl fullWidth>
                <InputLabel id="category-label">Categoria</InputLabel>
                <Select
                  labelId="category-label"
                  label="Categoria"
                  value={formData.category_id}
                  onChange={(e) =>
                    setFormData({ ...formData, category_id: e.target.value })
                  }
                  data-testid="product-category-select"
                >
                  <MenuItem value="">Selecione...</MenuItem>
                  {safeCategories.map((cat) => (
                    <MenuItem key={cat.id} value={cat.id}>
                      {cat.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth>
                <InputLabel id="supplier-label">Fornecedor</InputLabel>
                <Select
                  labelId="supplier-label"
                  label="Fornecedor"
                  value={formData.supplier_id}
                  onChange={(e) =>
                    setFormData({ ...formData, supplier_id: e.target.value })
                  }
                  data-testid="product-supplier-select"
                >
                  <MenuItem value="">Selecione...</MenuItem>
                  {safeSuppliers.map((sup) => (
                    <MenuItem key={sup.id} value={sup.id}>
                      {sup.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Linha 3: Unidade / Preço de Custo */}
              <FormControl fullWidth>
                <InputLabel id="unit-label">Unidade</InputLabel>
                <Select
                  labelId="unit-label"
                  label="Unidade"
                  value={formData.unit}
                  onChange={(e) =>
                    setFormData({ ...formData, unit: e.target.value })
                  }
                  required
                  data-testid="product-unit-select"
                >
                  <MenuItem value="un">Unidade</MenuItem>
                  <MenuItem value="kg">Quilograma</MenuItem>
                  <MenuItem value="l">Litro</MenuItem>
                  <MenuItem value="m">Metro</MenuItem>
                </Select>
              </FormControl>

              <TextField
                label="Preço de Custo"
                type="text"
                fullWidth
                required
                value={maskedValues.cost_price}
                onChange={(e) => handleCostChange(e.target.value)}
                inputProps={{ inputMode: "numeric" }}
                data-testid="product-cost-input"
              />

              {/* Linha 4: Preço de Venda / Lucro % */}
              <TextField
                label="Preço de Venda"
                type="text"
                fullWidth
                required
                value={maskedValues.sale_price}
                onChange={(e) => handleSalePriceChange(e.target.value)}
                inputProps={{ inputMode: "numeric" }}
                data-testid="product-price-input"
              />

              <TextField
                label="Lucro %"
                type="text"
                fullWidth
                value={maskedValues.profit_percentage}
                onChange={(e) => handleProfitChange(e.target.value)}
                helperText="Altere para recalcular o preço de venda"
                inputProps={{ inputMode: "numeric" }}
                data-testid="product-profit-input"
              />

              {/* Linha 5: Estoque Mínimo / Estoque Atual */}
              <TextField
                label="Estoque Mínimo"
                type="number"
                fullWidth
                required
                value={formData.min_stock}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    min_stock: parseInt(e.target.value, 10) || 0,
                  })
                }
                data-testid="product-minstock-input"
              />

              {/* Linha 5: Estoque Atual ocupando a linha inteira */}
              <TextField
                label="Estoque Atual"
                type="number"
                fullWidth
                required
                value={formData.stock}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    stock: parseInt(e.target.value, 10) || 0,
                  })
                }
                data-testid="product-stock-input"
              />
            </Box>

            <DialogActions sx={{ mt: 3 }}>
              <Button
                type="button"
                variant="outlined"
                onClick={() => {
                  setShowModal(false);
                  resetForm();
                }}
                data-testid="cancel-product-btn"
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                variant="contained"
                data-testid="submit-product-btn"
              >
                {editingProduct ? "Atualizar" : "Criar"}
              </Button>
            </DialogActions>
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
}
